import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import { useState } from 'react'
import {
  PiArrowsDownUpBold,
  PiCaretDown,
  PiCaretUp,
  PiCheck,
} from 'react-icons/pi'

export type FilterSortMode = 'category' | 'asc' | 'desc'

const OPTIONS: Array<{ value: FilterSortMode; label: string }> = [
  { value: 'category', label: 'Category' },
  { value: 'asc', label: 'A–Z' },
  { value: 'desc', label: 'Z–A' },
]

/**
 * Sort selector for long filter modals (Doors, Chains, Campaign Targets).
 * Mirrors the SelectSavedFilter pill style so it visually parents next to it
 * in the modal header. "Category" preserves the curated groups the filterset
 * author wrote in JSX; A–Z / Z–A flattens to a single list bucketed by the
 * first letter of each filter's title.
 */
export function FilterSortDropdown({
  value,
  onChange,
}: {
  value: FilterSortMode
  onChange: (next: FilterSortMode) => void
}) {
  const [open, setOpen] = useState(false)
  const selectedLabel =
    OPTIONS.find((o) => o.value === value)?.label ?? 'Category'

  return (
    // Below sm (640px) we compact to a square icon button — label + caret
    // hide so the header doesn't overflow on phones. Above sm the full
    // pill returns.
    <div className="relative flex-shrink-0">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Sort: ${selectedLabel}`}
            title={`Sort: ${selectedLabel}`}
            className={cn(
              'flex items-center h-9 rounded-full',
              'w-9 sm:w-auto justify-center sm:justify-start',
              // gap-3 (12px) on desktop — gap-2 felt squished after the
              // sort icon and before the caret. gap-0 on mobile since the
              // label + caret hide and only the icon remains.
              'gap-0 sm:gap-3 pl-0 pr-0 sm:pl-3 sm:pr-3',
              'text-sm font-medium transition-colors',
              'focus:outline-none focus-visible:outline-none',
              '!border',
              open
                ? '!bg-white !border-neutral-400 !text-neutral-800'
                : '!bg-neutral-50 !border-neutral-300 hover:!bg-white hover:!border-neutral-400 !text-neutral-700'
            )}
          >
            <PiArrowsDownUpBold
              size={14}
              className="flex-shrink-0 text-neutral-500"
            />
            {/* mx-1 (4px each side) on top of the parent gap-3 so the icon
                and caret get visible breathing room — gap alone read tight
                because `PiArrowsDownUpBold` has very little internal
                padding compared to e.g. PiFloppyDisk. */}
            <span className="hidden sm:inline whitespace-nowrap mx-1">
              Sort: {selectedLabel}
            </span>
            {open ? (
              <PiCaretUp
                size={14}
                className="hidden sm:block text-neutral-500"
              />
            ) : (
              <PiCaretDown
                size={14}
                className="hidden sm:block text-neutral-500"
              />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          className="!rounded-xl !border-neutral-200 !shadow-lg !bg-white w-[180px] !p-0 !overflow-hidden"
        >
          <div className="p-1.5">
            {OPTIONS.map((option) => {
              const active = option.value === value
              return (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => {
                    onChange(option.value)
                    setOpen(false)
                  }}
                  className={cn(
                    'w-full text-left flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
                    'hover:bg-neutral-100',
                    active
                      ? 'bg-neutral-100 text-neutral-900 font-semibold'
                      : 'text-neutral-700'
                  )}
                >
                  <span>{option.label}</span>
                  {active && (
                    <PiCheck size={14} className="text-brand-active" />
                  )}
                </button>
              )
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
